import { getProductCategoriesSelector } from "../../umbilical_hocs";
import { useContext, Fragment } from "react";
import { createUseStyles } from "react-jss";
import PropTypes from "prop-types";
import { StyleTypeContext, TranslationContext, isValidURL } from "../../umbilical_tools";
import _ from "lodash";

import { InlineIcon } from "@iconify/react";
import jssVariables from "../../jss_setup";
import {
  getFooterGenSectionsSelector,
  getTermsAndConditionsSelector,
  hasFAQ,
} from "../../selectors";
import { linksData } from "../navbars/ProductTypeSectionsLinks";
import PoweredBy from "../common/PoweredBy";
import { usePropertyLangTag } from "../../hooks";
import { useSelector } from "react-redux";
import { GlobalModalsContext } from "../../context";
import { useHistory } from "react-router-dom";

const footerLogo = import.meta.env.VITE_APP_FOOTER_LOGO;
const asset_s3_url = import.meta.env.VITE_APP_S3_ASSETS_URL || "";
let VERSION_TAG = import.meta.env.VITE_APP_VERSION_TAG;
if (VERSION_TAG.includes("-")) {
  VERSION_TAG = VERSION_TAG.split("-")[0];
}

const footerStyles = createUseStyles({
  footerContainer: (props: any) => ({
    width: "100%",
    position: "relative",
    paddingTop: "4rem",
    zIndex: 1,
    alignSelf: "end",
    ...(props?.customStyles?.footerContainer ?? {}),
  }),
  ftrBackground: (props) => ({
    position: "absolute",
    top: 0,
    left: 0,
    height: "100%",
    width: "100vw",
    objectFit: "cover",
    zIndex: -1,
    ..._.get(props, "customStyles.ftrBackground", {}),
  }),
  diagonalDiv: (props) => ({
    display: props.type === "type4" ? "grid" : "none",
    height: "39rem",
    background: "#F3F3F3",
    clipPath: "polygon(0 11%, 100% 0, 100% 100%, 0% 100%)",
    zIndex: 0,
    bottom: "0px",
    width: "100vw",
    position: "absolute",
    ..._.get(props, "customStyles.diagonalDiv", {}),
  }),
  footer: (props) => ({
    position: "relative",
    background: props.background,
    padding: "0 10vw",
    paddingTop: "4rem",
    paddingBottom: "4rem",
    display: "grid",
    zIndex: 1,
    height: "max-content",
    clipPath: props.type === "type4" ? "polygon(0 7%, 100% 0, 100% 100%, 0% 100%)" : "unset",
    gridGap: `calc(${jssVariables.normal_gap} * 2)`,
    ...(props?.customStyles?.footer ?? {}),
  }),
  section: (props) => ({
    display: "grid",
    gridGap: jssVariables.normal_gap,
    color: props.textColor,
    justifySelf: "start",
    ..._.get(props, "customStyles.section", {}),
  }),
  orderedSections: () => ({
    display: "grid",
    gridGap: jssVariables.half_gap,
    justifyContent: "space-between",
  }),
  sectionSocialMedia: (props) => ({
    ..._.get(props, "customStyles.sectionSocialMedia", {}),
  }),
  sectionSocialMediaTitle: (props) => ({
    ..._.get(props, "customStyles.sectionSocialMediaTitle", {}),
  }),
  socialMediaLabel: (props) => ({
    ..._.get(props, "customStyles.socialMediaLabel", {}),
  }),
  logo: (props) => ({
    maxHeight: "3rem",
    objectFit: "contain",
    justifySelf: "center",
    ..._.get(props, "customStyles.logo", {}),
  }),
  group: (props) => ({
    display: "grid",
    gridTemplateColumns: "max-content 1fr",
    alignItems: "center",
    gridGap: jssVariables.half_gap,
    fontSize: "larger",
    cursor: "pointer",

    "& .rs-icon": {
      fontSize: "x-large",
      color: `${props.textColor}!important`,
      textAlign: "center",
      ..._.get(props, "customStyles.icon", {}),
    },
    "&:link": {
      textDecoration: "none",
      color: "white",
    },
    "&:hover:visited": {
      textDecoration: "none",
      color: "white",
    },
    ..._.get(props, "customStyles.group", {}),
  }),
  groupContactInfo: (props) => ({
    ..._.get(props, "customStyles.groupContactInfo", {}),
  }),
  groupAddress: (props) => ({
    ..._.get(props, "customStyles.groupAddress", {}),
  }),
  groupNoIcon: (props) => ({
    ..._.get(props, "customStyles.groupNoIcon", {}),
  }),
  emptyIconSpace: (props) => ({
    display: "grid",
    width: `calc(${jssVariables.normal_gap} * 3)`,
    ..._.get(props, "customStyles.emptyIconSpace", {}),
  }),
  socialMediaGroup: (props) => ({
    color: "white",
    "&:link:hover": {
      textDecoration: "none",
      color: "white",
    },
    "&:visited": {
      textDecoration: "none",
      color: "white",
    },
    cursor: "pointer",
    ..._.get(props, "customStyles.socialMediaGroup", {}),
  }),
  groupIcon: (props) => ({
    justifySelf: "center",
    fontSize: "large",
    ..._.get(props, "customStyles.groupIcon", {}),
  }),
  quickLinkLabel: (props) => ({
    textTransform: "capitalize",
    ..._.get(props, "customStyles.quickLinkLabel", {}),
  }),
  socialMediaIcon: (props) => ({
    ..._.get(props, "customStyles.socialMediaIcon", {}),
  }),
  urlLinkText: (props) => ({
    color: jssVariables.colors.text.light,
    "&:hover": {
      color: jssVariables.colors.text.light,
    },
    ..._.get(props, "customStyles.urlLinkText", {}),
  }),
  phoneLinkToModal: (props) => ({
    cursor: "pointer",
    display: "grid",
    gridTemplateColumns: "max-content max-content",
    alignItems: "center",
    gridGap: `calc(${jssVariables.normal_gap} * 1)`,
    ..._.get(props, "customStyles.phoneLinkToModal", {}),
  }),
  phoneLinkViewMoreLabel: (props) => ({
    textTransform: "uppercase",
    fontSize: "small",
    ..._.get(props, "customStyles.phoneLinkViewMoreLabel", {}),
  }),
  extraLinks: (props) => ({
    display: "grid",
    gridGap: `calc(${jssVariables.normal_gap} * 2)`,
    padding: `calc(${jssVariables.normal_gap} * 2) calc(${jssVariables.normal_gap} * 3)`,
    background: "white",
    ..._.get(props, "customStyles.extraLinks", {}),
  }),
  extraLink: (props) => ({
    textTransform: "uppercase",
    fontSize: "medium",
    fontWeight: "bold",
    cursor: "pointer",
    ..._.get(props, "customStyles.extraLink", {}),
  }),
  logosContainerTTA: (props) => ({
    display: "grid",
    gridGap: `calc(${jssVariables.normal_gap} * 2)`,
    ..._.get(props, "customStyles.logosContainerTTA", {}),
  }),
  logosContainerTTARow2: (props) => ({
    display: "grid",
    gridTemplateColumns: "repeat(2, max-content)",
    gridGap: jssVariables.normal_gap,
    ..._.get(props, "customStyles.logosContainerTTARow2", {}),
  }),
  logoTTA: (props) => ({
    height: "5rem",
    ..._.get(props, "customStyles.logoTTA", {}),
  }),
  noCursorPointer: {
    cursor: "default!important",
  },
  quickLinksHeader: (props) => ({
    ...(props?.customStyles?.quickLinksHeader ?? {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    footerContainer: (props: any) => ({
      width: "100vw",
      ...(props?.customStyles?.footerContainer?.smallscreen ?? {}),
    }),
    footer: (props: any) => ({
      alignItems: "start",
      gridTemplateColumns: props.footerType === "type1" ? "repeat(4, 1fr)" : "1fr 3fr 1fr",
      height: "max-content",
      clipPath: props.type === "type4" ? "polygon(0 20%, 100% 0, 100% 100%, 0% 100%)" : "unset",
      paddingBottom: "4rem",
      ..._.get(props, "customStyles.footer.smallscreen", {}),
    }),
    diagonalDiv: (props: any) => ({
      display: props.type === "type4" ? "grid" : "none",
      height: "30rem",
      clipPath: "polygon(0 25%, 100% 0, 100% 100%, 0% 100%)",
      ..._.get(props, "customStyles.diagonalDiv.smallscreen", {}),
    }),
    logo: (props: any) => ({
      justifySelf: "start",
      ..._.get(props, "customStyles.logo.smallscreen", {}),
    }),
    extraLink: (props: any) => ({
      ..._.get(props, "customStyles.extraLink.smallscreen", {}),
    }),
    logoTTA: (props: any) => ({
      ..._.get(props, "customStyles.logoTTA.smallscreen", {}),
    }),
    emptyIconSpace: (props: any) => ({
      ..._.get(props, "customStyles.emptyIconSpace.smallscreen", {}),
    }),
    group: (props: any) => ({
      ...(props?.customStyles?.group?.smallscreen ?? {}),
    }),
    extraLinks: (props: any) => ({
      display: "grid",
      gridTemplateColumns: "repeat(3, max-content)",
      ..._.get(props, "customStyles.extraLinks.smallscreen", {}),
    }),
    section: (props: any) => ({
      ..._.get(props, "customStyles.section.smallscreen", {}),
    }),
    orderedSections: (props: any) => ({
      width: "100%",
      gridAutoFlow: props.footerType === "type2" ? "column" : "row",
      justifySelf: "center",
    }),
    quickLinkLabel: (props: any) => ({
      ..._.get(props, "customStyles.quickLinkLabel.smallscreen", {}),
    }),
    sectionSocialMedia: (props: any) => ({
      ..._.get(props, "customStyles.sectionSocialMedia.smallscreen", {}),
    }),
  },
  [`@media ${jssVariables.media.bigscreen}`]: {
    footer: (props: any) => ({
      ...(props?.customStyles?.footer?.bigscreen ?? {}),
    }),
  },
});
// eslint-disable-next-line no-inner-declarations
function Footer(props: any) {
  const history = useHistory();
  const langTag = usePropertyLangTag();

  const {
    bookingProcess,
    contact_information,
    contact_informations,
    product_types,
    ordered_sections,
    list_pages,
    footerType,
    termsAndConditions,
    secondary_logo,
    withFAQ,
  } = useSelector((state: { WebsiteMetadata: any; HotelSearch: any; GuestProfile: any }) => {
    const termsAndConditions = getTermsAndConditionsSelector(state);
    const withFAQ = hasFAQ(state);

    const footerType = state.WebsiteMetadata?.metadata?.footer?.footer_type ?? "type1";

    return {
      bookingProcess: state?.HotelSearch?.bookingProcess ?? false,
      endpoint: state.GuestProfile?.extProfile?.endpoint,
      contact_information: state?.WebsiteMetadata?.metadata?.contact_information ?? {},
      contact_informations: state?.WebsiteMetadata?.metadata?.contact_informations ?? [],
      product_types: getProductCategoriesSelector(state),
      ordered_sections: getFooterGenSectionsSelector(state),
      list_pages: (state.WebsiteMetadata?.metadata?.list_pages ?? []).map((pg: string) =>
        _.lowerCase(pg),
      ),
      termsAndConditions,
      footerType,
      secondary_logo: state?.WebsiteMetadata?.metadata?.secondary_logo,
      withFAQ,
    };
  });
  const { type, brand, cStyler } = useContext(StyleTypeContext) as {
    type: string;
    brand: string;
    cStyler: Function;
  };
  const { setShowContactUsModal } = useContext(GlobalModalsContext) as {
    setShowContactUsModal: Function;
  };

  const { t } = useContext(TranslationContext);

  const { address, phone, emergency_phone, email } = contact_information ?? {};

  let { facebook_url, instagram_url, twitter_url, weibo_url, linkedin_url, tiktok_url } =
    contact_information ?? {};

  const customStyles = cStyler({ type, brand, objName: "footer" });
  const classes = footerStyles({ ...props, type, brand, footerType, customStyles });

  function isSocialAvailable() {
    return (
      [facebook_url, instagram_url, twitter_url, weibo_url, linkedin_url, tiktok_url].filter(
        (l) => l,
      ).length > 0
    );
  }
  let socialAvailable = isSocialAvailable();

  if (!socialAvailable) {
    for (const contact of contact_informations) {
      if (contact.facebook_url) {
        facebook_url = contact.facebook_url;
      }
      if (contact.instagram_url) {
        instagram_url = contact.instagram_url;
      }
      if (contact.twitter_url) {
        twitter_url = contact.twitter_url;
      }
      if (contact.weibo_url) {
        weibo_url = contact.weibo_url;
      }
      if (contact.linkedin_url) {
        linkedin_url = contact.linkedin_url;
      }
      if (contact.tiktok_url) {
        tiktok_url = contact.tiktok_url;
      }
    }
    socialAvailable = isSocialAvailable();
  }

  const withContactUsSection =
    ordered_sections.length && ordered_sections.some((s) => s.path === "contact_us");

  const logo = secondary_logo ?? footerLogo;

  return bookingProcess ? null : (
    <div className={classes.footerContainer}>
      {brand === "easy" && (
        <img className={classes.ftrBackground} alt="" src={`${asset_s3_url}/footer_bg.svg`} />
      )}
      <div className={classes.diagonalDiv} />
      <div className={classes.footer}>
        {brand === "easy" && (
          <img className={classes.logo} alt="" src={`${asset_s3_url}/easyTravel_logo.svg`} />
        )}
        <div className={classes.section}>
          {!["easy"].includes(brand) && <img className={classes.logo} alt="" src={logo} />}
          {!withContactUsSection && (
            <Fragment>
              {email && (
                <a className={classes.group} href={`mailto:${email}`}>
                  <InlineIcon icon="fa-solid:envelope" className={classes.groupIcon} />
                  <strong className={classes.groupContactInfo}>{email}</strong>
                </a>
              )}
              {phone && (
                <a className={classes.group} href={`tel:${phone}`}>
                  <InlineIcon icon="fa:phone" className={classes.groupIcon} />
                  <strong className={classes.groupContactInfo}>{phone}</strong>
                </a>
              )}
              {emergency_phone ? (
                <a className={classes.group} href={`tel:${emergency_phone}`}>
                  <InlineIcon icon="fa6-solid:triangle-exclamation" className={classes.groupIcon} />
                  <strong className={classes.groupContactInfo}>{emergency_phone} </strong>
                </a>
              ) : (
                ""
              )}
              {address && (
                <div
                  className={`${classes.group} ${classes.groupAddress} ${classes.noCursorPointer}`}
                >
                  <InlineIcon icon="fa:map-marker" className={classes.groupIcon} />
                  <strong>{address}</strong>
                </div>
              )}
            </Fragment>
          )}
        </div>
        <div className={`${classes.section} ${classes.orderedSections}`}>
          {!["easy", "innstanttravel", "mytravellifestyle", "hgv", "kemet"].includes(brand) && (
            <h4 className={classes.quickLinksHeader}>{t("quickLinks")}</h4>
          )}
          <div
            className={`${classes.group} ${classes.groupNoIcon}`}
            onClick={() => history.push("/")}
          >
            <strong className={classes.quickLinkLabel}>{t("home")}</strong>
          </div>
          <Fragment>
            {ordered_sections
              .filter((sect) => !sect.path.includes("privacy"))
              .map((sect, idx) => {
                return (
                  <div
                    key={`${idx}-${sect.path}`}
                    className={classes.group}
                    onClick={() => {
                      if (sect.path === "contact_us") {
                        setShowContactUsModal(true);
                      } else if (isValidURL(sect.path)) {
                        window.location.href = sect.path;
                      } else {
                        history.push(`/${sect.path}`);
                      }
                    }}
                  >
                    <strong className={classes.quickLinkLabel}>
                      {sect?.[`title${langTag}`] ?? ""}
                    </strong>
                  </div>
                );
              })}
          </Fragment>
          {![""].includes(brand) && (
            <Fragment>
              {!ordered_sections.length &&
                linksData.map(
                  ([label, pth, srv_type], idx) =>
                    product_types.includes(srv_type) &&
                    list_pages.includes(label) && (
                      <div
                        key={`${idx}-${pth}`}
                        className={classes.group}
                        onClick={() => history.push(`/${pth}`)}
                      >
                        <span className={classes.emptyIconSpace} />
                        <strong className={classes.quickLinkLabel}>{label}</strong>
                      </div>
                    ),
                )}
            </Fragment>
          )}
        </div>
        <div className={classes.section}>
          {ordered_sections
            .filter((sect) => sect.path.includes("privacy"))
            .map((sect, idx) => {
              return (
                <div
                  key={`${idx}-${sect.path}`}
                  className={classes.group}
                  onClick={() => {
                    if (sect.path === "contact_us") {
                      setShowContactUsModal(true);
                    } else if (isValidURL(sect.path)) {
                      window.location.href = sect.path;
                    } else {
                      history.push(`/${sect.path}`);
                    }
                  }}
                >
                  <strong className={classes.quickLinkLabel}>
                    {sect?.[`title${langTag}`] ?? ""}
                  </strong>
                </div>
              );
            })}
          {termsAndConditions.length > 0 && (
            <div
              className={`${classes.group} ${classes.groupNoIcon}`}
              onClick={() => history.push("/terms-and-conditions/")}
            >
              <strong className={classes.quickLinkLabel}>{t("termsAndConditions")}</strong>
            </div>
          )}
          {withFAQ && (
            <div
              className={`${classes.group} ${classes.groupNoIcon}`}
              onClick={() => history.push("/faq/")}
            >
              <strong className={classes.quickLinkLabel}>FAQ</strong>
            </div>
          )}
        </div>
        {socialAvailable && (
          <div className={`${classes.section} ${classes.sectionSocialMedia}`}>
            {[
              [facebook_url, "fa:facebook"],
              [instagram_url, "fa:instagram"],
              [twitter_url, "fa:twitter"],
              [weibo_url, "fa:weibo"],
              [linkedin_url, "fa:linkedin"],
              [tiktok_url, "fa-brands:tiktok"],
            ]
              .filter(([url, __]) => url)
              .map(([url, icon], idx) => (
                <a
                  key={`${idx}-${url}`}
                  className={`${classes.group} ${classes.socialMediaGroup}`}
                  rel="noreferrer"
                  target="_blank"
                  href={url}
                >
                  <InlineIcon
                    icon={icon}
                    className={`${classes.groupIcon} ${classes.socialMediaIcon}`}
                    href={url}
                  />
                </a>
              ))}
          </div>
        )}
        <span style={{ color: "white" }}>{VERSION_TAG}</span>
        {brand !== "travily" && <PoweredBy />}
      </div>
    </div>
  );
}
Footer.propTypes = {
  background: PropTypes.string.isRequired,
  textColor: PropTypes.string.isRequired,
};

export default Footer;
